import React from "react";
import { TextField as MuiTextField, TextFieldProps, SxProps, Theme } from "@mui/material";

interface CustomTextFieldProps extends Omit<TextFieldProps, "variant"> {
  variant?: "outlined";
  sx?: SxProps<Theme>;
}

/**
 * Standardized TextField component matching the SearchBar design
 * Uses transparent autofill override so Chrome won't paint its default autofill background.
 */
const TextField: React.FC<CustomTextFieldProps> = ({ sx, multiline, InputProps, size, ...props }) => {
  const isMultiline = Boolean(multiline);
  const hasStartIcon = Boolean(InputProps?.startAdornment);
  const hasEndIcon = Boolean(InputProps?.endAdornment);
  const hasIcon = hasStartIcon || hasEndIcon;
  const hasLabel = Boolean(props.label);
  const isSmall = size === "small";
  const isDateType = props.type === "date";

  const getInputBackground = (theme: Theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.04)";
  const getInputBorder = (theme: Theme) =>
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.12)"
      : "rgba(0,0,0,0.12)";

  const baseSx: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
      borderRadius: isMultiline ? 3 : 999,
      backgroundColor: getInputBackground,
      transition: "box-shadow 0.2s ease, background-color 0.2s ease",
      alignItems: isMultiline ? "flex-start" : "center",
      minHeight: isMultiline ? undefined : hasIcon ? (isSmall ? 40 : 50) : (isSmall ? 40 : 50),
      display: "flex",
      overflow: "hidden",
      "& fieldset": {
        borderColor: getInputBorder,
        borderWidth: "1px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: getInputBorder,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: getInputBorder,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: getInputBorder,
      },
      "&.Mui-focused": {
        boxShadow: "none",
        "& fieldset": {
          borderColor: getInputBorder,
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: getInputBorder,
        },
      },

      // ---------- Autofill: make Chrome's autofill transparent ----------
      // This uses a fully transparent color (#00000000) to prevent Chrome's default autofill overlay.
      "& input:-webkit-autofill, & input:-webkit-autofill:focus, & input:-webkit-autofill:hover, & input:-webkit-autofill:active": {
        WebkitBoxShadow: "0 0 0 1000px #00000000 inset !important",
        boxShadow: "0 0 0 1000px #00000000 inset !important",
        backgroundColor: "#00000000 !important",
        WebkitTextFillColor: (theme: Theme) => theme.palette.text.primary,
        caretColor: (theme: Theme) => theme.palette.text.primary,
        transition: "background-color 5000s ease-in-out 0s",
      },

      // Ensure adornments blend with the root when autofill applied (doesn't rely on :has())
      // These will be visible because the autofill overlay is made transparent above.
      "& .MuiInputAdornment-root": {
        height: "100%",
        maxHeight: "100%",
        alignItems: "center",
        backgroundColor: "transparent",
        transition: "background-color 0.2s ease",
        margin: 0,
        display: "flex",
      },
      "& .MuiInputAdornment-positionEnd": {
        borderRadius: isMultiline ? 3 : 999,
        paddingRight: isSmall ? 0.5 : 1,
        paddingLeft: isSmall ? 0.5 : 0.75,
        backgroundColor: "transparent",
        marginLeft: 0,
        marginRight: 0,
        height: "100%",
      },

      "& .MuiIconButton-root": {
        color: (theme: Theme) => theme.palette.text.secondary,
        backgroundColor: "transparent !important",
        borderRadius: "0 !important",
        "&:hover": {
          backgroundColor: "transparent !important",
        },
        "& .MuiSvgIcon-root": {
          fontSize: isSmall ? "1.25rem" : "1.5rem",
        },
      },
    },

    "& .MuiOutlinedInput-input": {
      py: isMultiline ? 0 : isSmall ? 1.25 : 1.75,
      pl: isMultiline ? 2.5 : hasStartIcon ? (isSmall ? 0 : 2) : (isSmall ? 2.5 : 3),
      pr: isMultiline ? 2.5 : hasEndIcon ? (isSmall ? 3 : 3.5) : (isSmall ? 2.5 : 3),
      lineHeight: 1.5,
      ...(isDateType && {
        py: 0,
        px: 2.5,
        height: isSmall ? 40 : 50,
        lineHeight: `${isSmall ? 40 : 50}px`,
      }),
    },
    "& .MuiOutlinedInput-inputMultiline": {
      py: 1.75,
      px: 2.5,
      lineHeight: 1.6,
    },
    "& .MuiOutlinedInput-root textarea": {
      padding: 0,
    },

    // Label styling - only apply when label prop is used
    ...(hasLabel && {
      "& .MuiInputLabel-root": {
        transform: isMultiline
          ? `translate(14px, ${isSmall ? 16 : 18}px) scale(1)`
          : hasStartIcon
          ? `translate(48px, ${isSmall ? 14 : 16}px) scale(1)`
          : `translate(14px, ${isSmall ? 14 : 16}px) scale(1)`,
        transformOrigin: "left top",
        transition: "transform 0.2s ease, color 0.2s ease",
        color: (theme: Theme) => theme.palette.text.secondary,
        fontSize: isSmall ? "0.875rem" : "0.90rem",
        lineHeight: 1.2,
        "&.MuiInputLabel-shrink": {
          transform: "translate(14px, -6px) scale(0.75)",
        },
      },
    }),

    ...(isDateType && {
      "& input[type='date']::-webkit-calendar-picker-indicator": {
        filter: (theme: Theme) =>
          theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
        opacity: 0.85,
        cursor: "pointer",
        transition: "opacity 0.2s ease",
        "&:hover": {
          opacity: 1,
        },
      },
      "& input[type='date']": {
        colorScheme: (theme: Theme) =>
          theme.palette.mode === "dark" ? "white" : "black",
      },
    }),
  };

  const combinedSx = Array.isArray(sx) ? [baseSx, ...sx] : sx ? [baseSx, sx] : [baseSx];

  return (
    <MuiTextField
      variant="outlined"
      multiline={multiline}
      size={size}
      InputProps={InputProps}
      sx={combinedSx}
      {...props}
    />
  );
};

export default TextField;
